import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigation } from '@react-navigation/native'
import { markModalAsShown, shouldShowModalOnce } from '@src/hooks/useOneTimeVisitedState'
import Storage from '@src/utils/storage'
import {
  EhrAppointmentAttendanceChargeKind,
  EhrAppointmentKind,
  EhrAppointmentStateKind,
  SurveysConfigKind,
} from '@src/types'
import User from '@src/utils/user'
import { useGetRequiredPolicyConsents } from '@src/screens/PolicyConsents/hooks/useGetRequiredPolicyConsents'
import { Feature, useFeatureFlag } from '@src/components/FeatureFlag'
import { userSelector } from '@src/selectors/app'
import { uiFlagsSelector } from '@src/selectors/uiFlags'
import { useLazyScreenPreload } from '@src/navigation/utils'
import {
  insurancePolicySelector,
  latestUserEhrAppointmentEarlierAvailableSlotsSelector,
  latestUserEhrAppointmentSelector,
  topPriorityCoachAssignmentSelector,
} from '../models/nutritionistHub.selectors'

const defaultNoShow = {
  shouldShow: () => false,
  markAsShown: () => {},
  getRoute: () => ({ name: '', params: undefined }),
  wasShown: () => false,
}

const useDefaultStateInteractions = (modalKey: string) => {
  const shouldShow = useCallback(() => shouldShowModalOnce(modalKey), [modalKey])

  const markAsShown = useCallback(() => markModalAsShown(modalKey), [modalKey])

  return { shouldShow, markAsShown }
}

const useClaimFreeAppointmentPromptInteractions = () => {
  const insurancePolicy = useSelector(insurancePolicySelector)
  const latestAppointment = useSelector(latestUserEhrAppointmentSelector)

  const modalKey = `${Storage.CLAIM_FREE_VIDEO_CALL_MODAL_VISITED_KEY}_${insurancePolicy?.id}`

  const { shouldShow, markAsShown } = useDefaultStateInteractions(modalKey)

  const latestAppointmentId = latestAppointment?.id
  const latestAppointmentKind = latestAppointment?.kind

  const getRoute = useCallback(() => {
    if (!latestAppointmentId) {
      return defaultNoShow.getRoute()
    }

    return {
      name: 'ClaimFreeVideoCallModal',
      params: {
        appointmentId: latestAppointmentId,
        appointmentKind: latestAppointmentKind,
      },
    }
  }, [latestAppointmentKind, latestAppointmentId])

  const wasShown = useCallback(() => !shouldShow(), [shouldShow])
  const attendance = latestAppointment?.attendances[0]

  if (
    !insurancePolicy ||
    !latestAppointment ||
    latestAppointment.lastState.kind !== EhrAppointmentStateKind.PendingSchedule ||
    attendance?.chargeKind !== EhrAppointmentAttendanceChargeKind.BsFree
  ) {
    return defaultNoShow
  }

  return {
    shouldShow,
    markAsShown,
    getRoute,
    wasShown,
  }
}

const useEhrInsurancePolicyRejectedPromptInteractions = () => {
  const {
    shouldShow: shouldShowFreeCallModal,
    wasShown: freeCallModalWasShown,
  } = useClaimFreeAppointmentPromptInteractions()
  const insurancePolicy = useSelector(insurancePolicySelector)

  const userWasGrantedFreeCall = shouldShowFreeCallModal() || freeCallModalWasShown()

  const modalKey = `${Storage.INSURANCE_POLICY_REJECTED_MODAL_VISITED_KEY}_${insurancePolicy?.id}`

  const { shouldShow, markAsShown } = useDefaultStateInteractions(modalKey)

  const getRoute = useCallback(() => {
    return {
      name: 'InsuranceNotEligibleModal',
    }
  }, [])

  if (!insurancePolicy || userWasGrantedFreeCall || !insurancePolicy.declined) {
    return defaultNoShow
  }

  return {
    shouldShow,
    markAsShown,
    getRoute,
  }
}

const useEhrEarlierSlotAvailablePromptInteractions = () => {
  const latestAppointment = useSelector(latestUserEhrAppointmentSelector)
  const user = useSelector(userSelector)

  const earlierAvailableSlotsCheckFeatureEnabled = useFeatureFlag(
    Feature.VideoCallsEarlierAvailableSlotsCheck,
  )

  const earlierAvailableSlots = useSelector(latestUserEhrAppointmentEarlierAvailableSlotsSelector)

  const earlierSlotsAvailable = earlierAvailableSlots.length > 0

  const modalKey = `${Storage.EARLIER_APPOINTMENT_SLOT_AVAILABLE_MODAL_VISITED_KEY}_${user?.id}`

  const { shouldShow, markAsShown } = useDefaultStateInteractions(modalKey)

  const latestAppointmentId = latestAppointment?.id
  const latestAppointmentKind = latestAppointment?.kind

  const getRoute = useCallback(() => {
    if (!latestAppointmentId || !latestAppointmentKind) {
      return defaultNoShow.getRoute()
    }

    return {
      name: 'EarlierSlotAvailableModal',
      params: {
        appointmentKind: latestAppointmentKind as EhrAppointmentKind,
        appointmentId: latestAppointmentId,
        allowBackNavigation: true,
      },
    }
  }, [latestAppointmentKind, latestAppointmentId])

  if (!latestAppointment || !earlierAvailableSlotsCheckFeatureEnabled || !earlierSlotsAvailable) {
    return defaultNoShow
  }

  return {
    shouldShow,
    markAsShown,
    getRoute,
  }
}

const useNutritionistSwitchPromptInteractions = () => {
  const dispatch = useDispatch()

  const isNutritionistSwitchFeatureFlagEnabled = useFeatureFlag(Feature.RdsSwitchWhenRdIsLeaving)

  const uiFlags = useSelector(uiFlagsSelector)
  const newCoachAssignment = useSelector(topPriorityCoachAssignmentSelector)
  // This flag will only be present if it was an auto switch
  const coachSwitchUiFlag = uiFlags.showNutritionistSwitchModal
  const postCoachSwitchSurveyUiFlag = uiFlags.showPostCoachSwitchSurvey

  const shouldShowCoachSwitchModal = isNutritionistSwitchFeatureFlagEnabled && coachSwitchUiFlag
  const shouldShowPostSwitchSurvey =
    isNutritionistSwitchFeatureFlagEnabled && postCoachSwitchSurveyUiFlag

  useLazyScreenPreload('CoachSwitchSummaryScreen', {
    shouldPreload: shouldShowCoachSwitchModal && !!newCoachAssignment,
  })
  useLazyScreenPreload('NutritionistSwitchModal', {
    shouldPreload: shouldShowCoachSwitchModal && !newCoachAssignment,
  })

  const getRoute = useCallback(() => {
    if (shouldShowPostSwitchSurvey) {
      dispatch({
        type: 'uiFlags/updateUiFlags',
        payload: { showPostCoachSwitchSurvey: false },
      })

      return {
        name: 'Questionnaire',
        params: { questionnaire: SurveysConfigKind.PostCoachSwitch },
      }
    }

    if (newCoachAssignment) {
      return {
        name: 'CoachSwitchSummaryScreen',
        params: {
          userInitiated: false,
          coachId: newCoachAssignment.coach?.id,
        },
      }
    }

    return {
      name: 'NutritionistSwitchModal',
    }
  }, [newCoachAssignment, shouldShowPostSwitchSurvey, dispatch])

  return {
    shouldShow: shouldShowCoachSwitchModal || shouldShowPostSwitchSurvey,
    getRoute,
  }
}

export const useEhrNutritionistHubModals = () => {
  const navigation = useNavigation()

  const {
    shouldShow: shouldShowClaimFreeVideoCallPrompt,
    markAsShown: markClaimFreeVideoPrompt,
    getRoute: getClaimFreeVideoPromptRoute,
  } = useClaimFreeAppointmentPromptInteractions()

  const {
    shouldShow: shouldShowInsuranceRejectedPrompt,
    markAsShown: markInsuranceRejectedPrompt,
    getRoute: getInsuranceRejectedPromptRoute,
  } = useEhrInsurancePolicyRejectedPromptInteractions()

  const {
    shouldShow: shouldShowEarlierSlotAvailablePrompt,
    markAsShown: markEarlierSlotAvailablePromptAsShown,
    getRoute: getEarlierSlotAvailablePromptRoute,
  } = useEhrEarlierSlotAvailablePromptInteractions()

  const {
    shouldShow: showNutritionistSwitch,
    getRoute: getNutritionistSwitchRoute,
  } = useNutritionistSwitchPromptInteractions()

  const { getRequiredPolicyConsents } = useGetRequiredPolicyConsents()

  const handleModals = useCallback(async () => {
    if (User.isImpersonating()) {
      // using the useImpersonationContext leads to a race condition that shows the welcome tour during impersonation
      return
    }

    const showClaimFreeVideoCallPrompt = shouldShowClaimFreeVideoCallPrompt()
    const showInsuranceRejectedPrompt = shouldShowInsuranceRejectedPrompt()
    const showEarlierSlotAvailablePrompt = shouldShowEarlierSlotAvailablePrompt()

    const routes: { name: string; params?: any }[] = []

    if (showNutritionistSwitch) {
      routes.push(getNutritionistSwitchRoute())
    }

    if (showClaimFreeVideoCallPrompt) {
      routes.push(getClaimFreeVideoPromptRoute())
      markClaimFreeVideoPrompt()
    }

    if (showInsuranceRejectedPrompt) {
      routes.push(getInsuranceRejectedPromptRoute())
      markInsuranceRejectedPrompt()
    }

    if (showEarlierSlotAvailablePrompt) {
      routes.push(getEarlierSlotAvailablePromptRoute())
      markEarlierSlotAvailablePromptAsShown()
    }

    // Routes will be pushed in reverse order and we always want the consent modals to show first if present.
    // So, this should be the last item added to the stack.
    //
    const requiredPolicies = await getRequiredPolicyConsents()
    if (requiredPolicies.length > 0) {
      routes.push({ name: 'RequiredPolicyConsent' })
    }

    routes.forEach(({ name, params }) => {
      navigation.navigate(name as any, params)
    })
  }, [
    shouldShowClaimFreeVideoCallPrompt,
    shouldShowInsuranceRejectedPrompt,
    shouldShowEarlierSlotAvailablePrompt,
    showNutritionistSwitch,
    getRequiredPolicyConsents,
    getClaimFreeVideoPromptRoute,
    markClaimFreeVideoPrompt,
    getInsuranceRejectedPromptRoute,
    markInsuranceRejectedPrompt,
    getEarlierSlotAvailablePromptRoute,
    markEarlierSlotAvailablePromptAsShown,
    getNutritionistSwitchRoute,
    navigation,
  ])

  return { handleModals }
}
